import React from "react";
import { useDocument } from "../loan_tool";
import CurrencyField from "../../fields/currency_field";
import PercentField from "../../fields/percent_field";
import NumberField from "../../fields/number_field";

// ── Helpers ────────────────────────────────────────────────────────────────────

function num(val) {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
}

function sumFields(data, ...keys) {
  return keys.reduce((acc, k) => acc + num(data[k]), 0);
}

function formatDollars(n) {
  if (n === 0) return "$0";
  return "$" + Math.round(n).toLocaleString("en-US");
}

function formatPct(n) {
  return n.toFixed(2) + "%";
}

function calcMonthlyPayment(loanAmount, annualRatePct, type) {
  const loan = num(loanAmount);
  const rate = num(annualRatePct) / 100;
  if (loan === 0 || rate === 0) return 0;
  const monthly = rate / 12;
  if (type === "PI") {
    return loan * monthly / (1 - Math.pow(1 + monthly, -360));
  }
  return loan * monthly;
}

// ── Defaults ───────────────────────────────────────────────────────────────────

const DEFAULT_PAGE3 = {
  // Additional Repairs
  ext_fencing: "0", ext_driveway: "0", ext_landscaping: "0",
  ext_paint: "0", ext_deck: "0", ext_misc: "0",
  gar_door: "0", gar_floor: "0", gar_walls: "0",
  gar_electrical: "0", gar_misc: "0",
  roof_main: "0", roof_gutters: "0", roof_skylights: "0", roof_misc: "0",
  pool_resurfacing: "0", pool_plumbing: "0", pool_equipment: "0",
  pool_heater: "0", pool_pump: "0", pool_filter: "0", pool_lighting: "0", pool_misc: "0",
  mech_hvac: "0", mech_panel: "0", mech_plumbing: "0", mech_misc: "0",
  other_permits: "0", other_clean: "0", other_staging: "0",
  other_misc1: "0", other_misc2: "0",
  // Single Lender
  lender_ltv1: "80",
  lender_ltv2: "0",
  lender_rate1: "0",
  lender_rate1_type: "IO",
  lender_rate2: "0",
  lender_rate2_type: "IO",
  lender_closing_pct: "3.5",
  lender_earnest: "0",
  lender_concessions: "0",
  lender_addl_fee: "0",
  lender_mortgage_ins: "0",
  lender_mlsc: "0",
  lender_hoa1: "0",
  lender_hoa2: "0",
  // Utilities (monthly)
  util_gas: "0",
  util_electric: "0",
  util_water: "0",
  util_trash: "0"
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function TotalRow({ label, value }) {
  return (
    <div className="ff-total-row">
      <span className="ff-total-label">{label}</span>
      <span className="ff-total-value">{formatDollars(value)}</span>
    </div>
  );
}

function ReadRow({ label, value }) {
  return (
    <div className="ff-total-row" style={{ background: "#f0f4fa", borderColor: "#ccd8e8" }}>
      <span className="ff-total-label">{label}</span>
      <span className="ff-total-value">{value}</span>
    </div>
  );
}

function GrandRow({ label, value }) {
  return (
    <div className="ff-section-total">
      <span>{label}</span>
      <span>{formatDollars(value)}</span>
    </div>
  );
}

function RepairSection({ title, fields, data, onChange }) {
  const total = fields.reduce((acc, f) => acc + num(data[f.key]), 0);
  return (
    <div className="ff-room-box">
      <div className="ff-room-title">{title}</div>
      {fields.map(f => (
        <CurrencyField
          key={f.key}
          name={f.label}
          fieldName={f.key}
          fixedDigits={0}
          value={data[f.key] || "0"}
          onChange={onChange}
        />
      ))}
      <TotalRow label="Section Total" value={total} />
    </div>
  );
}

function SummaryCard({ label, value, highlight }) {
  return (
    <div className={highlight ? "ff-summary-card ff-summary-card-highlight" : "ff-summary-card"}>
      <div className="ff-summary-card-label">{label}</div>
      <div className="ff-summary-card-value">{formatDollars(value)}</div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function FixAndFlipPage3() {
  const [document, setDocument, { lastSaveDate }, { saveDocument }] = useDocument();

  const fix_and_flip = document.fix_and_flip || {};
  const page1 = fix_and_flip.page1 || {};
  const page2 = fix_and_flip.page2 || {};
  const page3 = { ...DEFAULT_PAGE3, ...fix_and_flip.page3 };

  function updatePage3(updatedFields) {
    setDocument({
      ...document,
      fix_and_flip: {
        ...fix_and_flip,
        page3: { ...page3, ...updatedFields }
      }
    });
  }

  function toggleRate1Type(type) {
    updatePage3({ lender_rate1_type: type });
  }

  function toggleRate2Type(type) {
    updatePage3({ lender_rate2_type: type });
  }

  // ── Additional Repair totals ────────────────────────────────────────────────
  const extTotal = sumFields(page3, "ext_fencing", "ext_driveway", "ext_landscaping", "ext_paint", "ext_deck", "ext_misc");
  const garTotal = sumFields(page3, "gar_door", "gar_floor", "gar_walls", "gar_electrical", "gar_misc");
  const roofTotal = sumFields(page3, "roof_main", "roof_gutters", "roof_skylights", "roof_misc");
  const poolTotal = sumFields(page3, "pool_resurfacing", "pool_plumbing", "pool_equipment", "pool_heater", "pool_pump", "pool_filter", "pool_lighting", "pool_misc");
  const mechTotal = sumFields(page3, "mech_hvac", "mech_panel", "mech_plumbing", "mech_misc");
  const otherTotal = sumFields(page3, "other_permits", "other_clean", "other_staging", "other_misc1", "other_misc2");
  const col2Subtotal = extTotal + garTotal + roofTotal + poolTotal + mechTotal + otherTotal;

  // ── Page 2 subtotal ─────────────────────────────────────────────────────────
  const col1Subtotal = Object.values(page2).reduce((acc, v) => {
    const n = parseFloat(v);
    return acc + (isNaN(n) ? 0 : n);
  }, 0);

  const totalRehabBudget = col1Subtotal + col2Subtotal;

  // ── Lender calculations ─────────────────────────────────────────────────────
  const purchasePrice = num((document.loans && document.loans[0]) ? document.loans[0].offer_price : "0");
  const salesPriceLender = num(page1.accepted_price) || purchasePrice;

  const ltv1 = num(page3.lender_ltv1);
  const ltv2 = num(page3.lender_ltv2);
  const loan1 = salesPriceLender * ltv1 / 100;
  const loan2 = salesPriceLender * ltv2 / 100;
  const combinedLTV = salesPriceLender > 0 ? (loan1 + loan2) / salesPriceLender * 100 : 0;
  const downPayment = salesPriceLender - loan1 - loan2;
  const downPaymentPct = salesPriceLender > 0 ? downPayment / salesPriceLender * 100 : 0;
  const closingCosts = salesPriceLender * num(page3.lender_closing_pct) / 100;
  const earnest = num(page3.lender_earnest);
  const totalCostToPurchase = downPayment + closingCosts + earnest;

  const moPay1 = calcMonthlyPayment(loan1, page3.lender_rate1, page3.lender_rate1_type);
  const moPay2 = calcMonthlyPayment(loan2, page3.lender_rate2, page3.lender_rate2_type);
  const propTaxPerYear = salesPriceLender * num(page1.property_tax_rate) / 100;
  const insuranceMo = num(page1.annual_insurance) / 12;
  const taxMo = propTaxPerYear / 12;
  const addlFee = num(page3.lender_addl_fee);
  const mortgageIns = num(page3.lender_mortgage_ins);
  const mlsc = num(page3.lender_mlsc);
  const hoa1 = num(page3.lender_hoa1);
  const hoa2 = num(page3.lender_hoa2);
  const totalPerMonth = moPay1 + moPay2 + addlFee + insuranceMo + taxMo + mortgageIns + mlsc + hoa1 + hoa2;

  const holdMonths = num(page1.estimated_hold_time) || 1;
  const totalLoanPayments = (moPay1 + moPay2) * holdMonths;
  const totalTaxInsurance = (taxMo + insuranceMo) * holdMonths;
  const utilTotal = (num(page3.util_gas) + num(page3.util_electric) + num(page3.util_water) + num(page3.util_trash)) * holdMonths;
  const financingAndHoldCost = closingCosts + earnest + totalLoanPayments + totalTaxInsurance;

  const grandTotal = purchasePrice + totalRehabBudget + financingAndHoldCost + utilTotal;

  const rate1Label = page3.lender_rate1_type === "PI" ? "1st Mo Pay — P&I" : "1st Mo Pay — I/O";
  const rate2Label = page3.lender_rate2_type === "PI" ? "2nd Mo Pay — P&I" : "2nd Mo Pay — I/O";

  function formatLastSaved() {
    if (!lastSaveDate) return "Not yet saved";
    return "Last saved: " + new Date(lastSaveDate).toLocaleTimeString();
  }

  return (
    <div>
      {/* Additional Repairs */}
      <div className="ff-section">
        <div className="ff-section-title">Additional Repairs &amp; Improvements</div>
        <div className="ff-rooms-layout">
          <div className="ff-rooms-col">
            <RepairSection
              title="Exterior / Yard / Landscape"
              fields={[
                { key: "ext_fencing", label: "Fencing" },
                { key: "ext_driveway", label: "Driveway / Walkways" },
                { key: "ext_landscaping", label: "Landscaping" },
                { key: "ext_paint", label: "Exterior Paint" },
                { key: "ext_deck", label: "Deck / Patio" },
                { key: "ext_misc", label: "Miscellaneous" }
              ]}
              data={page3}
              onChange={updatePage3}
            />
            <RepairSection
              title="Garage"
              fields={[
                { key: "gar_door", label: "Garage Door" },
                { key: "gar_floor", label: "Floor / Epoxy" },
                { key: "gar_walls", label: "Walls / Drywall" },
                { key: "gar_electrical", label: "Electrical" },
                { key: "gar_misc", label: "Miscellaneous" }
              ]}
              data={page3}
              onChange={updatePage3}
            />
            <RepairSection
              title="Roof"
              fields={[
                { key: "roof_main", label: "Roof Replacement" },
                { key: "roof_gutters", label: "Gutters" },
                { key: "roof_skylights", label: "Skylights" },
                { key: "roof_misc", label: "Miscellaneous" }
              ]}
              data={page3}
              onChange={updatePage3}
            />
          </div>
          <div className="ff-rooms-col">
            <RepairSection
              title="Pool / Spa"
              fields={[
                { key: "pool_resurfacing", label: "Resurfacing" },
                { key: "pool_plumbing", label: "Plumbing" },
                { key: "pool_equipment", label: "Equipment" },
                { key: "pool_heater", label: "Heater" },
                { key: "pool_pump", label: "Pool Pump" },
                { key: "pool_filter", label: "Filter" },
                { key: "pool_lighting", label: "Lighting" },
                { key: "pool_misc", label: "Miscellaneous" }
              ]}
              data={page3}
              onChange={updatePage3}
            />
            <RepairSection
              title="HVAC / Electrical / Plumbing"
              fields={[
                { key: "mech_hvac", label: "HVAC System" },
                { key: "mech_panel", label: "Electrical Panel" },
                { key: "mech_plumbing", label: "Plumbing" },
                { key: "mech_misc", label: "Miscellaneous" }
              ]}
              data={page3}
              onChange={updatePage3}
            />
            <RepairSection
              title="Other / Miscellaneous"
              fields={[
                { key: "other_permits", label: "Permits & Fees" },
                { key: "other_clean", label: "Construction Clean-up" },
                { key: "other_staging", label: "Staging" },
                { key: "other_misc1", label: "Miscellaneous 1" },
                { key: "other_misc2", label: "Miscellaneous 2" }
              ]}
              data={page3}
              onChange={updatePage3}
            />
          </div>
        </div>
      </div>

      {/* Column 2 Subtotal */}
      <div className="ff-col1-subtotal">
        <span>Additional Repairs Subtotal</span>
        <span className="ff-col1-subtotal-value">{formatDollars(col2Subtotal)}</span>
      </div>

      {/* Single Lender Financing */}
      <div className="ff-section">
        <div className="ff-section-title">Fix &amp; Flip Purchase Financing</div>
        <div className="ff-lender-section">

          {/* Left: Loan Structure */}
          <div>
            <div className="ff-lender-col-title">Loan structure</div>

            <ReadRow label="Sales price / market value" value={formatDollars(salesPriceLender)} />
            <div className="ff-sub-total-label">Auto-filled from Page 1 accepted price</div>

            <PercentField
              name="1st Loan LTV %"
              fieldName="lender_ltv1"
              fixedDigits={2}
              value={page3.lender_ltv1}
              onChange={updatePage3}
            />
            <TotalRow label="1st Loan Amount" value={loan1} />

            <PercentField
              name="2nd Loan LTV %"
              fieldName="lender_ltv2"
              fixedDigits={2}
              value={page3.lender_ltv2}
              onChange={updatePage3}
            />
            <TotalRow label="2nd Loan Amount" value={loan2} />
            <ReadRow label="Combined LTV" value={formatPct(combinedLTV)} />

            <div className="ff-toggle-row">
              <span className="ff-total-label">INT Rate — 1st</span>
              <div className="ff-rate-input-wrapper">
                <PercentField
                  name=""
                  fieldName="lender_rate1"
                  fixedDigits={3}
                  value={page3.lender_rate1}
                  onChange={updatePage3}
                />
                <div className="ff-toggle-group">
                  <button
                    className={"ff-toggle-btn" + (page3.lender_rate1_type === "PI" ? " active" : "")}
                    onClick={() => toggleRate1Type("PI")}
                  >P&amp;I</button>
                  <button
                    className={"ff-toggle-btn" + (page3.lender_rate1_type === "IO" ? " active" : "")}
                    onClick={() => toggleRate1Type("IO")}
                  >I/O</button>
                </div>
              </div>
            </div>
            <TotalRow label={rate1Label} value={moPay1} />

            <div className="ff-toggle-row">
              <span className="ff-total-label">INT Rate — 2nd</span>
              <div className="ff-rate-input-wrapper">
                <PercentField
                  name=""
                  fieldName="lender_rate2"
                  fixedDigits={3}
                  value={page3.lender_rate2}
                  onChange={updatePage3}
                />
                <div className="ff-toggle-group">
                  <button
                    className={"ff-toggle-btn" + (page3.lender_rate2_type === "PI" ? " active" : "")}
                    onClick={() => toggleRate2Type("PI")}
                  >P&amp;I</button>
                  <button
                    className={"ff-toggle-btn" + (page3.lender_rate2_type === "IO" ? " active" : "")}
                    onClick={() => toggleRate2Type("IO")}
                  >I/O</button>
                </div>
              </div>
            </div>
            <TotalRow label={rate2Label} value={moPay2} />

            <PercentField
              name="Closing Costs %"
              fieldName="lender_closing_pct"
              fixedDigits={3}
              value={page3.lender_closing_pct}
              onChange={updatePage3}
            />
            <TotalRow label="Closing Costs $" value={closingCosts} />

            <CurrencyField
              name="Earnest Money Deposit"
              fieldName="lender_earnest"
              fixedDigits={0}
              value={page3.lender_earnest}
              onChange={updatePage3}
            />
            <CurrencyField
              name="Seller Concessions"
              fieldName="lender_concessions"
              fixedDigits={0}
              value={page3.lender_concessions}
              onChange={updatePage3}
            />

            <ReadRow label="Down Payment %" value={formatPct(downPaymentPct)} />
            <TotalRow label="Down Payment $" value={downPayment} />

            <GrandRow label="Total Cost to Purchase" value={totalCostToPurchase} />
          </div>

          {/* Right: Monthly Costs */}
          <div>
            <div className="ff-lender-col-title">Monthly costs</div>

            <ReadRow label="Prop Tax Per Year" value={formatDollars(propTaxPerYear)} />
            <div className="ff-sub-total-label">Sales price × tax rate from Page 1</div>

            <ReadRow label={rate1Label} value={formatDollars(moPay1)} />
            <ReadRow label={rate2Label} value={formatDollars(moPay2)} />

            <CurrencyField
              name="Additional Mo Fee"
              fieldName="lender_addl_fee"
              fixedDigits={0}
              value={page3.lender_addl_fee}
              onChange={updatePage3}
            />

            <ReadRow label="Insurance (monthly)" value={formatDollars(insuranceMo)} />
            <div className="ff-sub-total-label">Annual insurance ÷ 12, from Page 1</div>

            <ReadRow label="Property Taxes (monthly)" value={formatDollars(taxMo)} />

            <CurrencyField
              name="Mortgage Insurance"
              fieldName="lender_mortgage_ins"
              fixedDigits={0}
              value={page3.lender_mortgage_ins}
              onChange={updatePage3}
            />
            <CurrencyField
              name="MLSC Mo. Cost"
              fieldName="lender_mlsc"
              fixedDigits={0}
              value={page3.lender_mlsc}
              onChange={updatePage3}
            />
            <CurrencyField
              name="H.O.A. Dues #1"
              fieldName="lender_hoa1"
              fixedDigits={0}
              value={page3.lender_hoa1}
              onChange={updatePage3}
            />
            <CurrencyField
              name="H.O.A. Dues #2"
              fieldName="lender_hoa2"
              fixedDigits={0}
              value={page3.lender_hoa2}
              onChange={updatePage3}
            />

            <GrandRow label="Total Per Month" value={totalPerMonth} />

            {/* Utilities */}
            <div className="ff-lender-col-title" style={{ marginTop: "16px" }}>
              Utilities during hold period (monthly)
            </div>
            <CurrencyField
              name="Gas"
              fieldName="util_gas"
              fixedDigits={0}
              value={page3.util_gas}
              onChange={updatePage3}
            />
            <CurrencyField
              name="Electric"
              fieldName="util_electric"
              fixedDigits={0}
              value={page3.util_electric}
              onChange={updatePage3}
            />
            <CurrencyField
              name="Water"
              fieldName="util_water"
              fixedDigits={0}
              value={page3.util_water}
              onChange={updatePage3}
            />
            <CurrencyField
              name="Trash"
              fieldName="util_trash"
              fixedDigits={0}
              value={page3.util_trash}
              onChange={updatePage3}
            />
            <TotalRow label={`Utilities Total (${holdMonths} mo)`} value={utilTotal} />

            {/* Hold Period Summary */}
            <div className="ff-lender-col-title" style={{ marginTop: "16px" }}>
              Hold period summary ({holdMonths} months)
            </div>
            <TotalRow label="Total Loan Payments" value={totalLoanPayments} />
            <TotalRow label="Tax + Insurance Total" value={totalTaxInsurance} />
            <TotalRow label="Utilities Total" value={utilTotal} />
            <GrandRow label="Total Financing &amp; Hold Cost" value={financingAndHoldCost} />
          </div>
        </div>
      </div>

      {/* Total Cost Analysis Summary — clean additive breakdown (matches Page 4
          "Project Cost Summary") so the LO / contractor can see at a glance how
          the Grand Total is built. Every line adds down to the bottom line. */}
      <div className="ff-section">
        <div className="ff-section-title">Total Cost Analysis Summary</div>
        <div style={{ padding: "12px 16px" }}>
          <TotalRow label="Purchase Price" value={purchasePrice} />
          <TotalRow label="Kitchen &amp; Rooms (Page 2)" value={col1Subtotal} />
          <TotalRow label="Additional Repairs (Page 3)" value={col2Subtotal} />
          <TotalRow label="Financing &amp; Hold Cost (Page 3)" value={financingAndHoldCost} />
          <TotalRow label="Utilities" value={utilTotal} />
        </div>
        <div style={{ padding: "0 16px 12px" }}>
          <GrandRow label="Grand Total Project Cost" value={grandTotal} />
        </div>
      </div>

      {/* Footer */}
      <div className="ff-footer">
        <span className="ff-last-saved">{formatLastSaved()}</span>
        <div className="ff-footer-actions">
          <button className="button" onClick={saveDocument}>
            Save to Loan File
          </button>
        </div>
      </div>
    </div>
  );
}
